import { ScorecardStructure } from "./Scorecard";
import planAData from "./mockData/planA.json";
import planBData from "./mockData/planB.json";

// Types for the new JSON format
interface DosimetricCriterion {
  Criterion: string;
  Outcome: string;
  Actual: string;
}

interface DVHStructure {
  Structure: string;
  IsTarget: boolean;
  DosimetricCriteria: DosimetricCriterion[];
}

interface PlanData {
  DVHStatistics: DVHStructure[];
  PlanApproval: {
    Timestamp: string;
  };
}

// Transform functions to convert new format to existing interface
const transformCriterion = (
  criterion: string,
): { criterion: string; tolerance?: number } => {
  // Keep dose units in cGy instead of converting to Gy
  // V4000cGy<=40%/+8% -> V4000cGy ≤ 40% (with tolerance 8)
  // D2%<=7276cGy -> D2% ≤ 7276cGy
  // V6800cGy>=95% -> V6800cGy ≥ 95%

  let transformed = criterion;
  let tolerance: number | undefined;

  // Extract tolerance value before removing it
  const toleranceMatch = criterion.match(/\/\+(\d+(?:\.\d+)?)(%|cGy|cm³)?/);
  if (toleranceMatch) {
    tolerance = parseFloat(toleranceMatch[1]);
    // Keep tolerance in original units (cGy, %, cm³)
  }

  // Replace operators
  transformed = transformed.replace(/<=/, " ≤ ");
  transformed = transformed.replace(/>=/, " ≥ ");
  transformed = transformed.replace(/=/, " = ");

  // Remove the secondary threshold (e.g., /+8% or /+200cGy or /+20cm³)
  transformed = transformed.replace(/\/\+\d+(?:\.\d+)?(%|cGy|cm³)?/, "");

  return { criterion: transformed, tolerance };
};

const transformActualValue = (actual: string): string => {
  // Keep actual values in their original units (cGy, %, cm³)
  return actual;
};

const mapOutcomeToStatus = (outcome: string): string => {
  switch (outcome) {
    case "Passed":
      return "success";
    case "Warning":
      return "warning";
    case "Failed":
      return "error";
    default:
      return "success";
  }
};

const transformPlanData = (planData: PlanData): ScorecardStructure[] => {
  const transformedStructures = planData.DVHStatistics.map((structure) => ({
    structure: structure.Structure,
    isTarget: structure.IsTarget,
    criteria: structure.DosimetricCriteria.map((criterion) => {
      const transformed = transformCriterion(criterion.Criterion);
      return {
        criterion: transformed.criterion,
        actual: transformActualValue(criterion.Actual),
        status: mapOutcomeToStatus(criterion.Outcome),
        tolerance: transformed.tolerance,
      };
    }),
  }));

  // Sort structures: PTV first, then CTV, then others
  return transformedStructures.sort((a, b) => {
    const aIsPTV = a.structure.startsWith("PTV");
    const bIsPTV = b.structure.startsWith("PTV");
    const aIsCTV = a.structure.startsWith("CTV");
    const bIsCTV = b.structure.startsWith("CTV");

    // PTV structures come first
    if (aIsPTV && !bIsPTV) return -1;
    if (!aIsPTV && bIsPTV) return 1;

    // If both are PTV or neither are PTV, check for CTV
    if (!aIsPTV && !bIsPTV) {
      // CTV structures come second (after PTV)
      if (aIsCTV && !bIsCTV) return -1;
      if (!aIsCTV && bIsCTV) return 1;
    }

    // Within the same category (PTV, CTV, or other), maintain alphabetical order
    return a.structure.localeCompare(b.structure);
  });
};

// Data transformation functions for scorecard
export const getSinglePlanScorecardData = (
  plan: string,
): ScorecardStructure[] => {
  if (plan === "planA") {
    return transformPlanData(planAData as PlanData);
  } else {
    return transformPlanData(planBData as PlanData);
  }
};

export const getComparisonScorecardData = (): ScorecardStructure[] => {
  const planATransformed = transformPlanData(planAData as PlanData);

  // Combine both plans into comparison format
  const combinedData: ScorecardStructure[] = [];

  // Create a map for easier lookup
  const planBMap = new Map<string, DVHStructure>();
  (planBData as PlanData).DVHStatistics.forEach((structure) => {
    planBMap.set(structure.Structure, structure);
  });

  planATransformed.forEach((structureA) => {
    const structureB = planBMap.get(structureA.structure);

    if (structureB) {
      // Find matching criteria between plans
      const combinedCriteria = structureA.criteria.map((criteriaA, index) => {
        const criteriaB = structureB.DosimetricCriteria[index];

        return {
          criterion: criteriaA.criterion,
          tolerance: criteriaA.tolerance, // Include tolerance from Plan A
          planA: {
            value: transformActualValue(criteriaA.actual || ""),
            status: criteriaA.status || "success",
          },
          planB: {
            value: transformActualValue(criteriaB?.Actual || ""),
            status: mapOutcomeToStatus(criteriaB?.Outcome || "Passed"),
          },
        };
      });

      combinedData.push({
        structure: structureA.structure,
        isTarget: structureA.isTarget,
        criteria: combinedCriteria,
      });
    }
  });

  return combinedData;
};
